
import { getReportFile, getReportList, generateReport, lookupCustomer } from '@/api';
import LookupCustomerCode from '@/components/jobBooking/AdvancedJobBooking/LookupCustomerCode.vue';
import DateSelect from '@/components/dashBoard/selectsFields/components/DateSelect/DateSelect.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import { format, utcToZonedTime } from 'date-fns-tz';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Header, User } from '@/models/main';
import { ReportRequestParams, ReportResponse } from '@/models/reports';
import { notificationErrorText, timeZoneDefault } from '@/utils/PortalConstants';
import { blobDownloader } from '@/utils/Common';
import * as Sentry from '@sentry/vue';
import { formatDateStringWithTZ } from '@/utils/InputProcessingUtilities';
import { mapState } from 'vuex';

@Component({
  components: {
    BackButton,
    DateSelect,
    LookupCustomerCode,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class KPIReport extends Vue {
  private isLoading = false;
  private isDownloadLoading = false;
  private dates: string[] = [];
  private kpiReport: ReportResponse[] = [];
  private user: User;
  private timeZone = 'Australia/Sydney';
  private formatDateStringWithTZ = formatDateStringWithTZ;

  private headers: Header[] = [
    { text: 'Id', value: 'id', align: 'left' },
    { text: 'Customer', value: 'customer', align: 'left' },
    { text: 'Title', value: 'title', align: 'left', sortable: false },
    { text: 'Type', value: 'type', align: 'left', sortable: false },
    { text: 'Created by', value: 'created_by', align: 'left' },
    { text: 'Created at', value: 'created_at', align: 'left' },
    { text: 'Approved at', value: 'approved_at', align: 'left' },
    { text: 'Download', value: 'link', align: 'left', sortable: false },
  ];

  //Customer code lookup related
  private customerCode = '';
  private autocompleteCustomer = lookupCustomer;
  private setCustomerCode(customerCode): void {
    if (customerCode) {
      this.customerCode = customerCode;
    }
  }
  private clearCustomerCode(): void {
    this.customerCode = '';
  }
  // Date lookup
  private changeDate(dates: string[]): void {
    this.dates = dates;
    this.getKPIReportList();
  }
  // KPI report
  private getKPIReportList() {
    this.isLoading = true;
    const params: ReportRequestParams = {
      month: this.dates,
      customer_codes: this.customerCode,
      report_type: 2,
    };
    getReportList(params)
      .then((res: ReportResponse[]) => {
        this.kpiReport = res;
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error getting KPI report',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.isLoading = false;
      });
  }

  /**
   * Generate a report on backend
   * Type = 2 for KPI report type
   * @private
   */
  private generateKPIReport() {
    // download loader should be used, but I don't want to spent time on template to implement it
    // so using isLoading for now
    this.isLoading = true;
    this.isDownloadLoading = true;
    const params: ReportRequestParams = {
      month: this.dates,
      customer_codes: this.customerCode,
      report_type: 2,
    };
    generateReport(params)
      .then(() => {
        this.$notify({
          group: 'network',
          title: 'KPI report requested',
          text: 'Wait for the result. It could take up to 10-15m in case of big Report.',
          type: 'success',
        });
        this.getKPIReportList();
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error generation KPI report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.isDownloadLoading = false;
      });
  }

  /**
   * Download a report file by report ID
   * @param report_id
   * @private
   */
  private downloadKPIReport(report_id: number) {
    // download loader should be used, but I don't want to spent time on template to implement it
    // so using isLoading for now
    this.isLoading = true;
    this.isDownloadLoading = true;
    getReportFile(report_id)
      .then(res => {
        blobDownloader(res);
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error getting KPI report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.isDownloadLoading = false;
      });
  }

  private mounted(): void {
    const userTimeZone = this.user.timezone || timeZoneDefault;
    this.dates = [format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd')];
  }
}
